package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.setup.ReconnectRoutes
import co.maytes.standupbot.dashboard.features.setup.rememberReconnectLauncher
import co.maytes.standupbot.dashboard.features.standupruns.formatRelative
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.calendar.GoogleCalendarListDto
import co.maytes.standupbot.shared.dto.calendar.UpdateGoogleCalendarRequestDto
import co.maytes.standupbot.shared.dto.integration.HealthStateDto
import co.maytes.standupbot.shared.dto.integration.HealthTone
import co.maytes.standupbot.shared.dto.integration.IntegrationDto
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.IntegrationStatusDto
import co.maytes.standupbot.shared.dto.integration.TestCalendarResponseDto
import co.maytes.standupbot.shared.dto.integration.effectiveState
import co.maytes.standupbot.shared.dto.integration.healthBadge
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

@Composable
fun IntegrationStatusPanel(
    workspaceId: String?,
    provider: IntegrationProviderDto,
    store: IntegrationsStore,
    first: Boolean = false,
) {
    val integration = store.statusOf(provider)
    val auth = rememberAuthStore()
    val scope = rememberCoroutineScope()
    val ui = rememberUiStore()
    var confirmOpen by remember { mutableStateOf(false) }

    val suspended = provider == IntegrationProviderDto.GITHUB &&
        auth.currentUser?.workspace?.githubSuspended == true
    val linked = integration.isLinked()
    val rendered = renderStatus(provider, integration, suspended)
    val name = IntegrationsCopy.displayName(provider)

    Column(modifier = Modifier.id("integration-${IntegrationsCopy.slug(provider)}").fillMaxWidth()) {
        SettingsRowItem(
            title = name,
            first = first,
            description = rowDescription(rendered, integration, linked),
            leading = { provider.brand()?.let { BrandLogo(it) } },
            titleTrailing = { Badge(label = rendered.badgeLabel, tone = rendered.badgeTone) },
            trailing = {
                InstallActionLink(
                    workspaceId = workspaceId,
                    provider = provider,
                    installed = linked,
                    actionLabel = rendered.actionLabel,
                    emphasized = rendered.actionEmphasized,
                )
                if (linked) {
                    Button(
                        label = "Disconnect",
                        variant = ButtonVariant.DangerOutline,
                        size = ButtonSize.Sm,
                        onClick = { confirmOpen = true },
                    )
                }
            },
        )

        if (linked) IntegrationErrorLine(integration)

        if (provider == IntegrationProviderDto.GOOGLE_CALENDAR &&
            integration.isUsable() &&
            workspaceId != null
        ) {
            GoogleCalendarControls(workspaceId = workspaceId, store = store)
        }
    }

    if (confirmOpen) {
        ConfirmModal(
            title = "Disconnect $name?",
            body = IntegrationsCopy.disconnectBody(provider),
            confirmLabel = "Disconnect",
            tone = ConfirmTone.Danger,
            onConfirm = {
                confirmOpen = false
                val ws = workspaceId ?: return@ConfirmModal
                scope.launch {
                    when (val res = store.disconnect(ws, provider)) {
                        is Result.Ok ->
                            ui.showToast("$name disconnected", kind = ToastKind.Success)
                        is Result.Err ->
                            ui.showToast(
                                "Could not disconnect $name: " +
                                    res.error.displayMessage,
                                kind = ToastKind.Error,
                            )
                    }
                }
            },
            onDismiss = { confirmOpen = false },
        )
    }
}

@Composable
private fun InstallActionLink(
    workspaceId: String?,
    provider: IntegrationProviderDto,
    installed: Boolean,
    actionLabel: String?,
    emphasized: Boolean,
) {
    if (provider == IntegrationProviderDto.ANTHROPIC || provider == IntegrationProviderDto.UNKNOWN) return
    val launcher = rememberReconnectLauncher()
    val scope = rememberCoroutineScope()
    var launching by remember { mutableStateOf(false) }
    val baseLabel = actionLabel ?: defaultActionLabel(provider, installed)
    Button(
        label = if (launching) "Redirecting…" else baseLabel,
        variant = installVariant(installed, emphasized),
        size = ButtonSize.Sm,
        enabled = !launching,
        onClick = {
            val wid = workspaceId ?: return@Button
            scope.launch {
                launching = true
                val started = launcher.launch(provider, wid, IntegrationsCopy.displayName(provider), ReconnectRoutes.INTEGRATIONS) { }
                if (!started) launching = false
            }
        },
    )
}

private fun defaultActionLabel(provider: IntegrationProviderDto, installed: Boolean): String =
    when {
        installed -> "Reinstall"
        provider == IntegrationProviderDto.GITHUB -> "Install GitHub App"
        provider == IntegrationProviderDto.GOOGLE_CALENDAR -> "Connect"
        else -> "Install"
    }

private fun installVariant(installed: Boolean, emphasized: Boolean): ButtonVariant =
    when {
        installed && !emphasized -> ButtonVariant.Ghost
        installed -> ButtonVariant.Primary
        else -> ButtonVariant.Secondary
    }

@Composable
private fun TestCalendarAction(workspaceId: String) {
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    var inFlight by remember { mutableStateOf(false) }
    Button(
        label = if (inFlight) "Testing…" else "Test connection",
        variant = ButtonVariant.Ghost,
        size = ButtonSize.Sm,
        enabled = !inFlight,
        onClick = {
            scope.launch {
                inFlight = true
                val res = api.post<TestCalendarResponseDto>(
                    "/api/v1/workspaces/$workspaceId/integrations/google/test",
                )
                inFlight = false
                when (res) {
                    is Result.Ok -> {
                        val body = res.value
                        if (body.success) {
                            val sample = body.sampleTitles.take(3).joinToString(" · ")
                            val msg = if (sample.isNotBlank()) {
                                "Loaded ${body.eventCount} events: $sample"
                            } else {
                                "Loaded ${body.eventCount} events from Google Calendar"
                            }
                            ui.showToast(msg, kind = ToastKind.Success)
                        } else {
                            ui.showToast(
                                body.error ?: "Google Calendar test failed",
                                kind = ToastKind.Error,
                            )
                        }
                    }
                    is Result.Err -> ui.showToast(
                        "Couldn't reach Google Calendar: ${res.error.displayMessage}",
                        kind = ToastKind.Error,
                    )
                }
            }
        },
    )
}

private sealed interface CalendarListLoad {
    data object Loading : CalendarListLoad
    data class Ready(val list: GoogleCalendarListDto) : CalendarListLoad
    data object Failed : CalendarListLoad
}

@Composable
private fun GoogleCalendarControls(
    workspaceId: String,
    store: IntegrationsStore,
) {
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    var loadState by remember(workspaceId) { mutableStateOf<CalendarListLoad>(CalendarListLoad.Loading) }
    var saving by remember { mutableStateOf(false) }
    var reloadKey by remember { mutableStateOf(0) }

    LaunchedEffect(workspaceId, reloadKey) {
        loadState = CalendarListLoad.Loading
        loadState = when (
            val res = api.get<GoogleCalendarListDto>(
                "/api/v1/workspaces/$workspaceId/integrations/google/calendars",
            )
        ) {
            is Result.Ok -> CalendarListLoad.Ready(res.value)
            is Result.Err -> CalendarListLoad.Failed
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(top = Spacing.sm)
            .styleModifier { property("gap", Spacing.sm.toString()) },
    ) {
        SettingsField(label = "Calendar") {
            when (val state = loadState) {
                is CalendarListLoad.Loading ->
                    SpanText(
                        text = "Loading…",
                        modifier = Modifier.fontSize(Typography.label.size).color(Palette.textTertiary),
                    )
                is CalendarListLoad.Failed -> SettingsError(what = "the calendar list") { reloadKey += 1 }
                is CalendarListLoad.Ready -> {
                    val list = state.list
                    val current = list.currentCalendarId?.takeIf { it.isNotBlank() } ?: "primary"
                    val options = buildOptions(list)
                    Select(
                        value = current,
                        options = options.map { SelectOption(it.id, it.label) },
                        onValueChange = { newId ->
                            if (newId == current || saving) return@Select
                            val label = options.firstOrNull { it.id == newId }?.label ?: newId
                            scope.launch {
                                saving = true
                                val res = api.patch<UpdateGoogleCalendarRequestDto, Unit>(
                                    path = "/api/v1/workspaces/$workspaceId/integrations/google/calendar",
                                    body = UpdateGoogleCalendarRequestDto(calendarId = newId),
                                )
                                saving = false
                                when (res) {
                                    is Result.Ok -> {
                                        ui.showToast("Calendar updated to $label", kind = ToastKind.Success)
                                        reloadKey += 1
                                        store.refresh(workspaceId)
                                    }
                                    is Result.Err -> ui.showToast(
                                        "Couldn't update calendar: ${res.error.displayMessage}",
                                        kind = ToastKind.Error,
                                    )
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth().styleModifier { property("max-width", MAX_SELECT_WIDTH) },
                        disabled = saving,
                        ariaLabel = "Calendar",
                    )
                }
            }
        }
        Row(modifier = Modifier.fillMaxWidth()) { TestCalendarAction(workspaceId) }
    }
}

private data class CalendarOption(val id: String, val label: String)

private fun buildOptions(list: GoogleCalendarListDto): List<CalendarOption> {
    val seen = mutableSetOf<String>()
    val result = mutableListOf(CalendarOption(id = "primary", label = "Primary"))
    seen.add("primary")
    list.items.forEach { item ->
        if (item.id == "primary" || item.primary) return@forEach
        if (!seen.add(item.id)) return@forEach
        result.add(CalendarOption(id = item.id, label = item.summary))
    }
    val current = list.currentCalendarId?.takeIf { it.isNotBlank() }
    if (current != null && current !in seen) {
        result.add(CalendarOption(id = current, label = current))
    }
    return result
}

@Composable
private fun IntegrationErrorLine(integration: IntegrationDto?) {
    val message = integration?.lastError ?: return
    Row(modifier = Modifier.fillMaxWidth().margin(top = Spacing.xs)) {
        SpanText(
            text = "Last error: $message",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontFamily(*Typography.MONO_STACK)
                .color(Palette.status(StatusHue.Danger).solid),
        )
    }
}

private data class RenderedIntegration(
    val badgeLabel: String,
    val badgeTone: BadgeTone,
    val subline: String,
    val actionLabel: String? = null,
    val actionEmphasized: Boolean = false,
)

private fun rowDescription(rendered: RenderedIntegration, integration: IntegrationDto?, linked: Boolean): String? {
    val healthy = linked && rendered.badgeTone == BadgeTone.Success
    return listOfNotNull(
        rendered.subline.takeIf { it.isNotBlank() && !healthy }?.let { IntegrationsCopy.compact(it) },
        IntegrationsCopy.syncCaption(integration?.lastSyncAt, integration?.lastEventCount, Clock.System.now())
            .takeIf { linked },
    ).joinToString(" · ").ifBlank { null }
}

private fun IntegrationDto?.isLinked(): Boolean =
    this != null && status != IntegrationStatusDto.DISCONNECTED && status != IntegrationStatusDto.NOT_CONNECTED

private fun IntegrationDto?.isUsable(): Boolean =
    this != null && (status == IntegrationStatusDto.CONNECTED || status == IntegrationStatusDto.DEGRADED)

private fun HealthTone.toBadgeTone(): BadgeTone = when (this) {
    HealthTone.SUCCESS -> BadgeTone.Success
    HealthTone.WARNING -> BadgeTone.Warning
    HealthTone.ERROR -> BadgeTone.Error
    HealthTone.NEUTRAL -> BadgeTone.Neutral
}

private fun renderStatus(
    provider: IntegrationProviderDto,
    integration: IntegrationDto?,
    suspended: Boolean,
): RenderedIntegration {
    if (integration == null) {
        return RenderedIntegration(
            badgeLabel = "Not connected",
            badgeTone = BadgeTone.Neutral,
            subline = IntegrationsCopy.tagline(provider),
        )
    }
    val badge = integration.healthBadge()
    val state = integration.effectiveState()
    val summary = integration.health?.summary?.takeIf { it.isNotBlank() }
    val subline = when {
        suspended -> "Suspended on GitHub. Unsuspend the app to resume."
        state == HealthStateDto.HEALTHY -> buildConnectedSubline(integration)
        state == HealthStateDto.NOT_CONFIGURED ->
            if (integration.status == IntegrationStatusDto.DISCONNECTED) {
                "No longer linked to this workspace. Reinstall to reconnect."
            } else {
                IntegrationsCopy.tagline(provider)
            }
        else -> summary ?: legacySubline(integration.status)
    }
    return RenderedIntegration(
        badgeLabel = if (suspended) "Suspended" else badge.label,
        badgeTone = if (suspended) BadgeTone.Warning else badge.tone.toBadgeTone(),
        subline = subline,
        actionLabel = integration.health?.fix?.label?.takeIf { state != HealthStateDto.HEALTHY },
        actionEmphasized = state == HealthStateDto.NEEDS_RECONNECT || state == HealthStateDto.DEGRADED,
    )
}

private fun legacySubline(status: IntegrationStatusDto): String = when (status) {
    IntegrationStatusDto.NEEDS_REAUTH -> "Authorization expired. Reinstall to restore access."
    IntegrationStatusDto.DEGRADED -> "Working intermittently. Check back soon or reinstall."
    else -> "Connection error. Try reinstalling."
}

private fun buildConnectedSubline(integration: IntegrationDto): String {
    val parts = buildList {
        integration.externalTeamName?.takeIf { it.isNotBlank() }?.let { add(it) }
        integration.connectedAt?.let { add("installed ${formatRelative(it)}") }
    }
    return parts.joinToString(" · ")
}

private const val MAX_SELECT_WIDTH = "17.5rem"
